import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  clubsService,
  type CreateClubRequest,
  type ClubFilters,
} from "@/api/services/clubs.service";

export const clubKeys = {
  all: ["clubs"] as const,
  lists: () => [...clubKeys.all, "list"] as const,
  list: (filters?: ClubFilters) => [...clubKeys.lists(), filters] as const,
  filters: () => [...clubKeys.all, "filters"] as const,
};

export function useClubs(filters?: ClubFilters) {
  return useQuery({
    queryKey: clubKeys.list(filters),
    queryFn: () => clubsService.list(filters),
    staleTime: 30_000,
  });
}

/** Distinct league + country values used to populate the filter dropdowns. */
export function useClubFilterOptions() {
  return useQuery({
    queryKey: clubKeys.filters(),
    queryFn: () => clubsService.getFilters(),
    staleTime: 5 * 60_000,
  });
}

export function useCreateClub() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateClubRequest) => clubsService.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clubKeys.all });
    },
  });
}

export function useUpdateClub() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateClubRequest> }) =>
      clubsService.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clubKeys.all });
    },
  });
}

export function useDeleteClub() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => clubsService.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: clubKeys.all });
    },
  });
}
